/** Shared reporting charts (Live chat, Tickets, Chatbot). Uses ui-card + dark mode tokens. */

export function KpiGrid({ items }) {
  if (!items?.length) return null;
  return (
    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
      {items.map((m) => (
        <article key={m.id} className="ui-card">
          <div className="flex items-start justify-between gap-3">
            <div
              className="flex h-11 w-11 items-center justify-center rounded-2xl shadow-inner"
              style={{ backgroundColor: `${m.color}14` }}
            >
              <i className={`${m.icon} text-xl`} style={{ color: m.color }} aria-hidden />
            </div>
            <span
              className={`max-w-[55%] truncate rounded-full px-2.5 py-1 text-right text-xs font-semibold tabular-nums ${
                m.trendUp
                  ? "bg-emerald-50 text-emerald-700 ring-1 ring-emerald-200/60 dark:bg-emerald-950/50 dark:text-emerald-300 dark:ring-emerald-800/60"
                  : "bg-amber-50 text-amber-800 ring-1 ring-amber-200/60 dark:bg-amber-950/40 dark:text-amber-200 dark:ring-amber-800/50"
              }`}
              title={m.trend}
            >
              {m.trend}
            </span>
          </div>
          <p className="mt-4 text-3xl font-semibold tracking-tight text-slate-900 tabular-nums dark:text-slate-100">
            {m.value}
          </p>
          <p className="mt-1 text-sm font-medium text-slate-500 dark:text-slate-400">{m.label}</p>
        </article>
      ))}
    </div>
  );
}

export function ShareList({ title, subtitle, rows, emptyText }) {
  return (
    <div className="ui-card">
      <h2 className="text-base font-semibold text-slate-900 dark:text-slate-100">{title}</h2>
      <p className="mt-0.5 text-xs text-slate-500 dark:text-slate-400">{subtitle}</p>
      <div className="mt-6 space-y-4">
        {!rows?.length ? (
          <p className="text-sm text-slate-500 dark:text-slate-400">{emptyText}</p>
        ) : (
          rows.map((s) => (
            <div key={s.label}>
              <div className="mb-1.5 flex items-center justify-between text-xs font-medium">
                <span className="text-slate-600 dark:text-slate-400">{s.label}</span>
                <span className="tabular-nums text-slate-900 dark:text-slate-100">
                  {typeof s.count === "number" ? (
                    <>
                      <span className="font-semibold">{s.count}</span>
                      <span className="text-slate-400 dark:text-slate-500"> · </span>
                    </>
                  ) : null}
                  {s.value}%
                </span>
              </div>
              <div className="h-2 overflow-hidden rounded-full bg-slate-100 ring-1 ring-slate-200/60 dark:bg-slate-800 dark:ring-slate-700/60">
                <div
                  className="h-full rounded-full transition-all duration-500"
                  style={{ width: `${s.value}%`, backgroundColor: s.color }}
                />
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
}

export function VolumeBars({ title, subtitle, days, max, keys, legend }) {
  return (
    <div className="ui-card xl:col-span-2">
      <h2 className="text-base font-semibold text-slate-900 dark:text-slate-100">{title}</h2>
      <p className="mt-0.5 text-xs text-slate-500 dark:text-slate-400">{subtitle}</p>
      <div className="mt-6 flex h-56 gap-1.5 sm:gap-2">
        {(days ?? []).map((d, idx) => (
          <div key={`${d.date ?? ""}-${d.day ?? ""}-${idx}`} className="flex min-h-0 min-w-0 flex-1 flex-col items-center gap-2">
            <div className="flex min-h-0 w-full flex-1 items-end justify-center gap-0.5">
              {keys.map((k) => (
                <div
                  key={k.key}
                  className={`min-h-[8px] w-[30%] max-w-[18px] rounded-t-md shadow-sm sm:w-[32%] ${k.className}`}
                  style={{ height: `${((d[k.key] ?? 0) / max) * 100}%` }}
                />
              ))}
            </div>
            <span className="shrink-0 text-xs font-medium text-slate-400 dark:text-slate-500">{d.day}</span>
          </div>
        ))}
      </div>
      <div className="mt-4 flex flex-wrap gap-4 text-xs font-medium text-slate-500 dark:text-slate-400">
        {legend.map((L) => (
          <span key={L.label} className="flex items-center gap-2">
            <span className={`h-2.5 w-2.5 rounded-full shadow-sm ${L.dot}`} />
            {L.label}
          </span>
        ))}
      </div>
    </div>
  );
}
